import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config';
import { useNotification } from './Notification';
import { useResponsive } from '../hooks/useResponsive';
import { useAuth } from '../contexts/AuthContext';
import { AppHeader } from './AppHeader';
import { ProfileEditModal } from './ProfileEditModal';
import { DeleteAccountModal } from './DeleteAccountModal';
import { UserBadges } from './UserBadges';
import { Rankings } from './Rankings';
import { GlobalStats } from './GlobalStats';
import { UserReviewsResponse, UserMatchWithReviews, GamificationStats } from '../types';
import { ProfileHeader } from './ProfileHeader';
import { UserReviewsCard } from './UserReviewsCard';
import { UserTagsCard } from './UserTagsCard';
import { TopTagsCard } from './TopTagsCard';
import { ProfileStats } from './ProfileStats';
import PerformanceInsights from './PerformanceInsights';
import { MatchHistory } from './MatchHistory';
import { ProfileTabs, TabType, SubSectionType } from './ProfileTabs';
import { AchievementGrid } from './achievements/AchievementGrid';
import { ReferralCard } from './ReferralCard';
import type { Achievement } from '../types/achievement';
import { ProfileCarousel } from './ProfileCarousel';
import { UnifiedHelpButton, SpecificHelpModal } from './Help';
import StatsHistory from './StatsHistory';
import './ProfilePage.css';

interface UserStats {
  totalGoals: number;
  totalAssists: number;
  totalSaves: number;
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
  mvpCount: number;
  topScorerCount: number;
  topAssisterCount: number;
  topSaverCount: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  position?: string;
  rating: number;
  avatar?: string;
}


const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [reviewsDesktopHeight, setReviewsDesktopHeight] = useState(0);
  const { user: authUser, setUser: setAuthUser } = useAuth(); // Usar el usuario del AuthContext
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [matchHistory, setMatchHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [activeSubSection, setActiveSubSection] = useState<SubSectionType>('fifa-card');
  const [userReviews, setUserReviews] = useState<UserReviewsResponse | null>(null);
  const [gamificationStats, setGamificationStats] = useState<GamificationStats | null>(null);
  const [userBadges, setUserBadges] = useState<any[]>([]);
  const [myAchievements, setMyAchievements] = useState<Achievement[]>([]);
  const [rankings, setRankings] = useState<any>(null);
  const [showBadges, setShowBadges] = useState(false);
  const [showRankings, setShowRankings] = useState(false);
  const [showGlobalStats, setShowGlobalStats] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Control para evitar llamadas duplicadas
  const [fetchingData, setFetchingData] = useState(false); // Control para evitar llamadas en paralelo
  const [fetchingReviews, setFetchingReviews] = useState(false);
  const [fetchingStats, setFetchingStats] = useState(false);
  const [fetchingBadges, setFetchingBadges] = useState(false);
  const [profileInitialized, setProfileInitialized] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();
  const { isMobile } = useResponsive();

  // Cargar datos cuando el usuario esté disponible
  useEffect(() => {
    if (authUser && !user) {
      setUser(authUser);
      setDataLoaded(false); // Resetear para cargar datos del nuevo usuario
      setFetchingData(false);
      setFetchingReviews(false);
      setFetchingStats(false);
      setFetchingBadges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, user?.id]); // Solo dependencia del ID, no del objeto completo

  // Cargar datos principales cuando el usuario esté disponible
  useEffect(() => {
    if (profileInitialized) return; // Evitar ejecución múltiple en StrictMode
    
    if (user?.id && !dataLoaded) {
      setProfileInitialized(true);
      fetchUserData();
      fetchUserReviews();
      fetchGamingStats();
      fetchUserBadges();
      setDataLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, dataLoaded, profileInitialized]);

  // Recargar datos cuando cambie la página
  useEffect(() => {
    if (user?.id && dataLoaded) {
      fetchUserData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, user?.id, dataLoaded]);

  // Recargar badges cuando cambie la pestaña
  useEffect(() => {
    if (activeTab === 'rewards' && user?.id && dataLoaded) {
      fetchUserBadges();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.id, dataLoaded]);

  useEffect(() => {
    if (isMobile || activeTab !== 'profile') {
      setReviewsDesktopHeight(0);
    }
  }, [isMobile, activeTab]);

  const fetchUserData = async () => {
    if (fetchingData) {
      return;
    }
    try {
      setFetchingData(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        showNotification('Debes iniciar sesión para ver tu perfil', 'error');
        return;
      }

      // Usar el usuario del AuthContext si está disponible
      const currentUser = authUser || user;
      if (!currentUser) {
        showNotification('Error obteniendo información del usuario', 'error');
        return;
      }

      // Solo hacer la llamada a /me si no tenemos el usuario del AuthContext
      if (!authUser) {
        const userResponse = await fetch(getApiUrl('/api/auth/me'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!userResponse.ok) {
          showNotification('Error obteniendo información del usuario', 'error');
          return;
        }

        const userData = await userResponse.json();
        setUser(userData);
      }

      const userId = currentUser.id;
      if (!userId) {
        showNotification('Error obteniendo ID del usuario', 'error');
        return;
      }

      // Obtener estadísticas
      const statsResponse = await fetch(getApiUrl(`/api/users/${userId}/stats`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        // La respuesta incluye { user, stats }, necesitamos extraer solo stats
        setStats(statsData.stats || statsData);
      } else {
        const errorData = await statsResponse.text();
        // console.error('Stats response error:', errorData);
      }

      // Obtener historial de partidos
      const historyResponse = await fetch(
        getApiUrl(`/api/users/${userId}/match-history?page=${currentPage}&limit=5`),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        // El backend devuelve el historial en "matches", no en "history"
        setMatchHistory(historyData.matches ?? []);
        setTotalPages(historyData.pagination?.pages ?? 1);
      }

    } catch (error) {
      // console.error('Error fetching user data:', error);
      showNotification('Error cargando datos del perfil', 'error');
    } finally {
      setLoading(false);
      setFetchingData(false);
    }
  };

  const fetchUserReviews = async () => {
    if (fetchingReviews) {
      return;
    }
    try {
      setFetchingReviews(true);
      const userId = user?.id;
      if (!userId) return;

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/reviews/user/${userId}/received`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserReviews(data);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando reviews', 'error');
      }
    } catch (error) {
      // console.error('Error fetching user reviews:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setFetchingReviews(false);
    }
  };

  const fetchGamingStats = async () => {
    if (fetchingStats) {
      return;
    }
    try {
      setFetchingStats(true);
      const userId = user?.id;
      if (!userId) return;

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/gamification/user/${userId}/gaming-stats`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGamificationStats(data.stats);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando estadísticas de gaming', 'error');
      }
    } catch (error) {
      // console.error('Error fetching gaming stats:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setFetchingStats(false);
    }
  };

  const fetchUserBadges = async () => {
    if (fetchingBadges) {
      return;
    }
    try {
      setFetchingBadges(true);
      const userId = user?.id;
      if (!userId) return;

      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/gamification/user/${userId}/badges`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserBadges(data.badges || []);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando badges', 'error');
      }

      try {
        const achievementsRes = await fetch(getApiUrl('/me/achievements'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (achievementsRes.ok) {
          const json = await achievementsRes.json();
          setMyAchievements(json.achievements || []);
        }
      } catch {
        // best-effort; locked-state grid is still rendered from the catalog
      }
    } catch (error) {
      // console.error('Error fetching user badges:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setFetchingBadges(false);
    }
  };

  const fetchRankings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/gamification/rankings'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRankings(data);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando rankings', 'error');
      }
    } catch (error) {
      // console.error('Error fetching rankings:', error);
      showNotification('Error de conexión', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPositionEmoji = (position: string) => {
    switch (position) {
      case 'Arquero': return '🥅';
      case 'Defensor': return '🛡️';
      case 'Mediocampista': return '⚽';
      case 'Delantero': return '🚀';
      default: return '👤';
    }
  };

  const getMatchResultColor = (matchResult?: 'WIN' | 'LOSS' | 'DRAW') => {
    switch (matchResult) {
      case 'WIN': return '#1a2e1a'; // Verde muy oscuro
      case 'LOSS': return '#2e1a1a'; // Rojo muy oscuro
      case 'DRAW': return '#2a2a2a'; // Gris oscuro
      default: return '#2C2F33'; // Color por defecto
    }
  };

  const updateProfile = async (userData: Partial<User>) => {
    setEditingProfile(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showNotification('Debes iniciar sesión para editar tu perfil', 'error');
        return;
      }

              const response = await fetch(getApiUrl('/api/users/profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        const result = await response.json();
        showNotification('Perfil actualizado exitosamente', 'success');
        setShowEditModal(false);
        

        
        // Actualizar el estado local y el AuthContext para que persista al navegar
        setUser(result.user);
        setAuthUser(result.user);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error al actualizar el perfil', 'error');
      }
    } catch (error) {
      // console.error('Error updating profile:', error);
      showNotification('Error al actualizar el perfil', 'error');
    } finally {
      setEditingProfile(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showNotification('Debes iniciar sesión', 'error');
        return;
      }

      const response = await fetch(getApiUrl('/api/users/me/account'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('Cuenta eliminada exitosamente', 'success');

        // Limpiar el token y redirigir
        localStorage.removeItem('authToken');

        // Esperar un momento para que se vea la notificación
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error al eliminar la cuenta', 'error');
      }
    } catch (error) {
      showNotification('Error al eliminar la cuenta. Intenta de nuevo.', 'error');
    }
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      showNotification('Por favor selecciona una imagen válida', 'error');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('La imagen debe ser menor a 5MB', 'error');
      return;
    }

    try {
      // Convertir imagen a base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Image = e.target?.result as string;

        // updateProfile ya hace setUser(result.user) con la URL corta que
        // devuelve el server (el avatar ahora se sirve por /api/avatars/:id,
        // no como base64). No pisamos ese estado con el base64 local.
        await updateProfile({ avatar: base64Image });

        // Limpiar el input
        event.target.value = '';
      };
      reader.readAsDataURL(file);
    } catch (error) {
      // console.error('Error processing image:', error);
      showNotification('Error al procesar la imagen', 'error');
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div>Cargando perfil...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-error">
        <div>No se pudo cargar el perfil</div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <AppHeader />
      
      {/* Título con botón de ayuda */}
      <div className={isMobile ? "profile-title-container-mobile" : "profile-title-container"}>
        <h1 className={isMobile ? "profile-title-mobile" : "profile-title"}>👤 Mi Perfil</h1>
      </div>
      
      <div className={isMobile ? "profile-content-mobile" : "profile-content"}>
        {/* Pestañas - Ahora arriba del contenido */}
        <ProfileTabs
          activeTab={activeTab}
          activeSubSection={activeSubSection}
          onTabChange={(tab: TabType, subSection?: SubSectionType) => {
            setActiveTab(tab);
            if (subSection) {
              setActiveSubSection(subSection);
            } else {
              // Establecer sub-sección por defecto según el tab
              switch (tab) {
                case 'profile':
                  setActiveSubSection('fifa-card');
                  break;
                case 'stats':
                  setActiveSubSection('main-stats');
                  break;
                case 'rewards':
                  setActiveSubSection('badges');
                  break;
                case 'history':
                  setActiveSubSection(null);
                  break;
              }
            }
          }}
          isMobile={isMobile}
        />

        {/* Contenido de las pestañas */}
        {activeTab === 'profile' && (
          <>
            {isMobile ? (
              // Layout móvil con sub-secciones
              <>
                {activeSubSection === 'fifa-card' && (
                  <div className="profile-section-mobile">
                    <ProfileHeader
                      user={user}
                      stats={stats}
                      onEditProfile={() => setShowEditModal(true)}
                      onAvatarChange={handleAvatarChange}
                      isMobile={isMobile}
                      onDeleteAccount={() => setShowDeleteModal(true)}
                    />
                  </div>
                )}
                
                {activeSubSection === 'reviews' && (
                  <div className="profile-section-mobile">
                    <UserReviewsCard
                      reviews={userReviews}
                      isMobile={isMobile}
                    />
                  </div>
                )}

                {activeSubSection === 'fifa-card' && (
                  <div className="profile-section-mobile" style={{ marginTop: '16px' }}>
                    <ReferralCard />
                  </div>
                )}
              </>
            ) : (
              // Layout desktop original
              <>
                {/* Layout principal: FIFA Card + Reviews */}
                <div className="profile-main-layout">
                  {/* Columna izquierda - FIFA Card */}
                  <div className="profile-main-column">
                    <ProfileHeader
                      user={user}
                      stats={stats}
                      onEditProfile={() => setShowEditModal(true)}
                      onAvatarChange={handleAvatarChange}
                      isMobile={isMobile}
                      onDeleteAccount={() => setShowDeleteModal(true)}
                      onHeightChange={(h) => {
                        if (!isMobile && activeTab === 'profile') {
                          setReviewsDesktopHeight((prev) => (prev !== h ? h : prev));
                        }
                      }}
                    />
                  </div>

                  {/* Columna derecha - Reviews */}
                  <div className="profile-main-column">
                    <UserReviewsCard
                      reviews={userReviews}
                      isMobile={isMobile}
                      fixedDesktopHeight={reviewsDesktopHeight > 0 ? reviewsDesktopHeight : undefined}
                    />
                  </div>
                </div>

                {/* ReferralCard debajo del layout principal */}
                <div style={{ marginTop: '24px' }}>
                  <ReferralCard />
                </div>
              </>
            )}
          </>
        )}

        {/* Nueva pestaña de Estadísticas */}
        {activeTab === 'stats' && (
          <>
            {isMobile ? (
              // Layout móvil con sub-secciones
              <>
                {activeSubSection === 'main-stats' && (
                  <div className="profile-section-mobile">
                    {user?.id && <PerformanceInsights userId={user.id} isMobile={isMobile} />}
                    <ProfileStats stats={stats} isMobile={isMobile} />
                  </div>
                )}
                
                {activeSubSection === 'match-history' && (
                  <div className="profile-section-mobile">
                    <MatchHistory
                      matches={matchHistory}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      isMobile={isMobile}
                    />
                  </div>
                )}
                
                {activeSubSection === 'tags' && (
                  <div className="profile-section-mobile">
                    <TopTagsCard userId={user?.id} title="Tu reputación" isMobile={isMobile} />
                    <div style={{ marginTop: 16 }}>
                      <UserTagsCard
                        gamificationStats={gamificationStats}
                        isMobile={isMobile}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Layout desktop - mostrar todo junto
              <>
                {user?.id && <PerformanceInsights userId={user.id} isMobile={isMobile} />}
                <ProfileStats stats={stats} isMobile={isMobile} />
                <div className="profile-two-column-layout">
                  <div className="profile-column-left">
                    <MatchHistory
                      matches={matchHistory}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      isMobile={isMobile}
                    />
                  </div>
                  <div className="profile-column-right">
                    <TopTagsCard userId={user?.id} title="Tu reputación" isMobile={isMobile} />
                    <div style={{ marginTop: 20 }}>
                      <UserTagsCard
                        gamificationStats={gamificationStats}
                        isMobile={isMobile}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Pestaña de Recompensas */}
        {activeTab === 'rewards' && (
          <>
            {isMobile ? (
              // Layout móvil con sub-secciones
              <>
                {activeSubSection === 'badges' && (
                  <div className="profile-section-mobile">
                    <div style={{
                      backgroundColor: '#23272A',
                      padding: '14px',
                      borderRadius: '18px',
                      border: '1px solid #33383B',
                    }}>
                      <h2 style={{ fontSize: '17px', fontWeight: 'bold', margin: '0 0 16px 0', color: '#00E676' }}>
                        🏆 Logros
                      </h2>
                      <AchievementGrid unlocked={myAchievements} />
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Layout desktop
              <div style={{
                backgroundColor: '#23272A',
                padding: '24px',
                borderRadius: '18px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                border: '1px solid #33383B',
              }}>
                <h2 style={{ fontSize: '22px', fontWeight: 'bold', margin: '0 0 20px 0', color: '#00E676' }}>
                  🏆 Logros
                </h2>
                <AchievementGrid unlocked={myAchievements} />
              </div>
            )}
          </>
        )}

        {/* Pestaña de Historial */}
        {activeTab === 'history' && (
          <StatsHistory userId={user.id} isMobile={isMobile} useMockData={false} />
        )}

        {/* Modales */}
        <ProfileEditModal
          isOpen={showEditModal}
          user={user}
          onClose={() => setShowEditModal(false)}
          onSave={updateProfile}
          isLoading={editingProfile}
        />

        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
        />

        <UserBadges
          badges={userBadges}
          isVisible={showBadges}
          onClose={() => setShowBadges(false)}
        />

        <Rankings
          rankings={rankings}
          isVisible={showRankings}
          onClose={() => setShowRankings(false)}
        />

        <GlobalStats
          isVisible={showGlobalStats}
          onClose={() => setShowGlobalStats(false)}
        />

        {/* Modal de Ayuda Contextual */}
        <SpecificHelpModal 
          isOpen={showHelp} 
          onClose={() => setShowHelp(false)} 
          context="profile"
        />


      </div>
    </div>
  );
};

export default ProfilePage; 